import React from "react";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import BusinessIcon from "@mui/icons-material/Business";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

import dayjs from "../common/date";
import { getInitials } from "../common/utils";
import { backgrounds } from "../theme/theme";

const formatShortDate = (date: Date): string =>
  dayjs(date).utc().format("MMM D, YYYY");

interface EngagementProps {
  engagement: any;
  isListView: boolean;
  onEngagementClick?: (publicId: string) => void;
}

function Engagement(props: EngagementProps) {
  const { engagement, isListView, onEngagementClick } = props;
  const isEnded = !!engagement.endDate;

  // List view - compact card
  if (isListView) {
    return (
      <Paper
        elevation={0}
        onClick={() => onEngagementClick?.(engagement.publicId)}
        sx={{
          p: 2,
          borderRadius: 2,
          border: "1px solid",
          borderColor: "divider",
          cursor: "pointer",
          "&:hover": { backgroundColor: backgrounds.surface },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Avatar
            sx={{
              bgcolor: isEnded ? "grey.400" : "primary.main",
              width: 48,
              height: 48,
              mr: 2,
            }}
          >
            {getInitials(engagement.clientName)}
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Typography variant="body1" fontWeight={600}>
              {engagement.clientName}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {engagement.shortDescription}
            </Typography>
          </Box>
          <ChevronRightIcon color="action" />
        </Box>
      </Paper>
    );
  }

  // Detail view - full card
  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 2,
        border: "1px solid",
        borderColor: "divider",
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 3,
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          color: "white",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <Avatar
            sx={{
              bgcolor: "rgba(255,255,255,0.2)",
              width: 56,
              height: 56,
              mr: 2,
              fontSize: 20,
              fontWeight: 600,
            }}
          >
            {getInitials(engagement.clientName)}
          </Avatar>
          <Box>
            <Typography variant="h5" fontWeight={600}>
              {engagement.clientName}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              {engagement.shortDescription}
            </Typography>
          </Box>
        </Box>
        <Chip
          label={isEnded ? "Ended" : "Active"}
          size="small"
          sx={{
            bgcolor: isEnded ? "rgba(255,255,255,0.3)" : "rgba(0,216,142,0.9)",
            color: "white",
            fontWeight: 500,
          }}
        />
      </Box>

      {/* Stats */}
      <Box sx={{ p: 3 }}>
        <Grid container spacing={3}>
          <Grid item xs={6} sm={3}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
              <CalendarTodayIcon
                sx={{ fontSize: 18, color: "text.secondary", mr: 1 }}
              />
              <Typography variant="caption" color="text.secondary">
                Start Date
              </Typography>
            </Box>
            <Typography variant="body1" fontWeight={500}>
              {formatShortDate(engagement.startDate)}
            </Typography>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
              <AccessTimeIcon
                sx={{ fontSize: 18, color: "text.secondary", mr: 1 }}
              />
              <Typography variant="caption" color="text.secondary">
                Hours/Week
              </Typography>
            </Box>
            <Typography variant="body1" fontWeight={500}>
              {engagement.numberOfHoursPerWeek} hrs
            </Typography>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
              <AttachMoneyIcon
                sx={{ fontSize: 18, color: "text.secondary", mr: 1 }}
              />
              <Typography variant="caption" color="text.secondary">
                Rate
              </Typography>
            </Box>
            <Typography variant="body1" fontWeight={500}>
              {engagement.currency}
              {engagement.hourlyRate}/hr
            </Typography>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
              <BusinessIcon
                sx={{ fontSize: 18, color: "text.secondary", mr: 1 }}
              />
              <Typography variant="caption" color="text.secondary">
                Currency
              </Typography>
            </Box>
            <Typography variant="body1" fontWeight={500}>
              {engagement.currency}
            </Typography>
          </Grid>
        </Grid>

        {engagement.description && (
          <>
            <Divider sx={{ my: 3 }} />
            <Typography
              variant="subtitle2"
              color="text.secondary"
              gutterBottom
            >
              Description
            </Typography>
            <Typography variant="body2" color="text.primary">
              {engagement.description}
            </Typography>
          </>
        )}
      </Box>
    </Paper>
  );
}

export default Engagement;
